import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { useMarketplace } from '../../context/MarketplaceContext';
import { useCoins } from '../../context/CoinContext';
import { isMarketplaceUser } from '../../utils/roleManager';
import { checkCoinRequirement, MARKETPLACE_FEES } from '../../utils/marketplaceFees';
import AuctionCard from './AuctionCard';
import Pagination from './Pagination';
import BottomNavigation from '../Layout/BottomNavigation';

const AuctionManagement = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { createAuction, placeBid } = useMarketplace();
  const { coins } = useCoins();
  const [myAuctions, setMyAuctions] = useState([]);
  const [filteredAuctions, setFilteredAuctions] = useState([]);
  const [myProducts, setMyProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, ending_soon, ended
  const [sortBy, setSortBy] = useState('newest'); // newest, ending_soon, highest_price, most_bids
  const [viewMode, setViewMode] = useState('grid'); // grid, list, compact
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  const [auctionForm, setAuctionForm] = useState({
    startingPrice: '',
    reservePrice: '',
    auctionType: 'highest_bid',
    duration: 24
  });

  // Status filters
  const statusFilters = [
    { value: 'all', label: 'ทั้งหมด', icon: '📋' },
    { value: 'active', label: 'กำลังดำเนินการ', icon: '🔥' },
    { value: 'ending_soon', label: 'ใกล้หมดเวลา', icon: '⏰' },
    { value: 'ended', label: 'หมดเวลา', icon: '⏸️' }
  ];

  // Sort options
  const sortOptions = [
    { value: 'newest', label: 'ใหม่ล่าสุด', icon: '🆕' },
    { value: 'ending_soon', label: 'ใกล้หมดเวลาก่อน', icon: '⏰' },
    { value: 'highest_price', label: 'ราคาสูงสุด', icon: '💰' },
    { value: 'most_bids', label: 'ผู้เสนอมากที่สุด', icon: '👥' }
  ];

  // View modes
  const viewModes = [
    { value: 'grid', label: 'Grid', icon: '⊞' },
    { value: 'list', label: 'List', icon: '☰' },
    { value: 'compact', label: 'Compact', icon: '☷' }
  ];

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
  }, [currentUser]);

  useEffect(() => {
    applyFilters();
  }, [myAuctions, searchTerm, statusFilter, sortBy]);

  // Adjust itemsPerPage based on view mode
  useEffect(() => {
    if (viewMode === 'grid') {
      setItemsPerPage(12); // 12 cards per page for grid
    } else if (viewMode === 'list') {
      setItemsPerPage(10); // 10 cards per page for list (more space per card)
    } else if (viewMode === 'compact') {
      setItemsPerPage(20); // 20 cards per page for compact (smaller cards)
    }
    setCurrentPage(1); // Reset to first page when changing view
  }, [viewMode]);

  const fetchData = async () => {
    if (!currentUser) return;

    setLoading(true);
    try {
      // ดึงร้านของฉัน
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.data();

      if (userData?.storeId) {
        // ดึงสินค้าที่สามารถเปิดประมูลได้
        const productsQuery = query(
          collection(db, 'products'),
          where('storeId', '==', userData.storeId),
          where('status', '==', 'active')
        );
        const productsSnapshot = await getDocs(productsQuery);
        setMyProducts(productsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })));

        // ดึงประมูลของฉัน
        const auctionsQuery = query(
          collection(db, 'auctions'),
          where('sellerId', '==', currentUser.uid)
        );
        const auctionsSnapshot = await getDocs(auctionsQuery);
        let auctionsData = auctionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Load product images for auctions that don't have images
        const auctionsWithImages = await Promise.all(
          auctionsData.map(async (auction) => {
            // If auction already has image, return as is
            if (auction.productImage || auction.productImages?.[0] || auction.images?.[0]) {
              return auction;
            }

            // Try to load from product
            if (auction.productId) {
              try {
                const productRef = doc(db, 'products', auction.productId);
                const productSnap = await getDoc(productRef);
                if (productSnap.exists()) {
                  const productData = productSnap.data();
                  if (productData.images && productData.images.length > 0) {
                    return {
                      ...auction,
                      productImage: productData.images[0],
                      productImages: productData.images
                    };
                  }
                }
              } catch (error) {
                console.error('Error loading product image for auction:', error);
              }
            }

            return auction;
          })
        );

        setMyAuctions(auctionsWithImages);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEndTime = (auction) => {
    if (!auction.endTime) return null;
    if (auction.endTime.toDate && typeof auction.endTime.toDate === 'function') {
      return auction.endTime.toDate();
    }
    if (auction.endTime instanceof Date) {
      return auction.endTime;
    }
    const parsed = new Date(auction.endTime);
    return !isNaN(parsed.getTime()) ? parsed : null;
  };

  const applyFilters = () => {
    let filtered = [...myAuctions];

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(auction => 
        auction.productName?.toLowerCase().includes(searchLower) ||
        auction.productId?.toLowerCase().includes(searchLower)
      );
    }

    // Status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(auction => {
        const endTime = getEndTime(auction);
        return auction.status === 'active' && 
               !auction.isEnded && 
               endTime && 
               endTime > new Date();
      });
    } else if (statusFilter === 'ending_soon') {
      filtered = filtered.filter(auction => {
        const endTime = getEndTime(auction);
        if (!endTime) return false;
        const hoursLeft = (endTime.getTime() - new Date().getTime()) / (1000 * 60 * 60);
        return auction.status === 'active' && 
               !auction.isEnded && 
               hoursLeft > 0 && 
               hoursLeft <= 24;
      });
    } else if (statusFilter === 'ended') {
      filtered = filtered.filter(auction => {
        const endTime = getEndTime(auction);
        return auction.isEnded || 
               (endTime && endTime <= new Date()) ||
               auction.status === 'ended';
      });
    }

    // Sort
    filtered.sort((a, b) => {
      if (sortBy === 'ending_soon') {
        const aEnd = getEndTime(a);
        const bEnd = getEndTime(b);
        if (!aEnd) return 1;
        if (!bEnd) return -1;
        return aEnd.getTime() - bEnd.getTime();
      } else if (sortBy === 'newest') {
        const aCreated = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const bCreated = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return bCreated.getTime() - aCreated.getTime();
      } else if (sortBy === 'highest_price') {
        return (b.currentBid || b.startingPrice || 0) - (a.currentBid || a.startingPrice || 0);
      } else if (sortBy === 'most_bids') {
        return (b.bids?.length || 0) - (a.bids?.length || 0);
      }
      return 0;
    });

    setFilteredAuctions(filtered);
    setCurrentPage(1);
  };

  const handleCreateAuction = async (e) => {
    e.preventDefault();

    if (!selectedProduct) {
      alert(t('auction.pleaseSelectProduct'));
      return;
    }

    const feeCheck = checkCoinRequirement(coins, 'CREATE_AUCTION');
    if (!feeCheck.hasEnough) {
      alert(feeCheck.message);
      return;
    }

    try {
      const endTime = new Date();
      endTime.setHours(endTime.getHours() + parseInt(auctionForm.duration));

      await createAuction({
        productId: selectedProduct.id,
        productName: selectedProduct.name,
        storeId: selectedProduct.storeId,
        startingPrice: parseFloat(auctionForm.startingPrice),
        reservePrice: auctionForm.reservePrice ? parseFloat(auctionForm.reservePrice) : null,
        auctionType: auctionForm.auctionType,
        duration: parseInt(auctionForm.duration),
        endTime: endTime
      });

      alert(t('auction.createAuctionSuccess'));
      setShowCreateForm(false);
      setSelectedProduct(null);
      setAuctionForm({
        startingPrice: '',
        reservePrice: '',
        auctionType: 'highest_bid',
        duration: 24
      });
      fetchData();
    } catch (error) {
      alert(t('common.saveError') + error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (!isMarketplaceUser(currentUser)) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="container mx-auto px-4">
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-600">{t('auction.noPermissionToCreate')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-4 md:py-6">
        {/* Header */}
        <div className="mb-4 md:mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">🔨 {t('auction.manageAuction')}</h1>
              <p className="text-sm md:text-base text-gray-600">
                จัดการประมูลสินค้าของคุณ
              </p>
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2.5 px-4 md:px-6 rounded-lg transition-colors shadow-md hover:shadow-lg flex items-center gap-2 whitespace-nowrap"
            >
              <span className="text-lg">+</span>
              <span>{t('auction.createNewAuction')}</span>
              <span className="text-xs bg-yellow-600 px-2 py-0.5 rounded">
                {MARKETPLACE_FEES.CREATE_AUCTION.default} {t('auction.coins')}
              </span>
            </button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-4">
            <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 text-center border-l-4 border-yellow-500">
              <div className="text-2xl md:text-3xl font-bold text-yellow-600">{myAuctions.length}</div>
              <div className="text-xs md:text-sm text-gray-600 mt-1">ประมูลทั้งหมด</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 text-center border-l-4 border-green-500">
              <div className="text-2xl md:text-3xl font-bold text-green-600">
                {myAuctions.filter(a => {
                  const endTime = a.endTime?.toDate?.() || new Date(a.endTime);
                  return a.status === 'active' && !a.isEnded && endTime > new Date();
                }).length}
              </div>
              <div className="text-xs md:text-sm text-gray-600 mt-1">กำลังดำเนินการ</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 text-center border-l-4 border-red-500">
              <div className="text-2xl md:text-3xl font-bold text-red-600">
                {myAuctions.filter(a => a.isEnded || a.status === 'ended').length}
              </div>
              <div className="text-xs md:text-sm text-gray-600 mt-1">หมดเวลา</div>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 text-center border-l-4 border-blue-500">
              <div className="text-2xl md:text-3xl font-bold text-blue-600">
                {myProducts.length}
              </div>
              <div className="text-xs md:text-sm text-gray-600 mt-1">สินค้าที่เปิดประมูลได้</div>
            </div>
          </div>
        </div>

        {/* Create Auction Form */}
        {showCreateForm && (
          <div className="bg-white rounded-lg shadow-lg p-4 md:p-6 mb-4 md:mb-6 border border-yellow-200">
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                <span>✨</span>
                <span>{t('auction.createNewAuction')}</span>
              </h2>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setSelectedProduct(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleCreateAuction}>
              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">{t('auction.selectProductLabel')}</label>
                <select
                  required
                  value={selectedProduct?.id || ''}
                  onChange={(e) => {
                    const product = myProducts.find(p => p.id === e.target.value);
                    setSelectedProduct(product);
                  }}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                >
                  <option value="">{t('auction.selectProduct')}</option>
                  {myProducts.map(product => (
                    <option key={product.id} value={product.id}>
                      {product.name} - ฿{product.price?.toLocaleString()}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">{t('auction.startingPrice')} *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={auctionForm.startingPrice}
                    onChange={(e) => setAuctionForm({ ...auctionForm, startingPrice: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">{t('auction.reservePrice')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={auctionForm.reservePrice}
                    onChange={(e) => setAuctionForm({ ...auctionForm, reservePrice: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    placeholder={t('auction.reservePricePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">{t('auction.auctionType')} *</label>
                  <select
                    required
                    value={auctionForm.auctionType}
                    onChange={(e) => setAuctionForm({ ...auctionForm, auctionType: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  >
                    <option value="highest_bid">{t('auction.auctionTypeHighestBid')}</option>
                    <option value="auction">{t('auction.auctionTypeGeneral')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">{t('auction.duration')} *</label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={auctionForm.duration}
                    onChange={(e) => setAuctionForm({ ...auctionForm, duration: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                >
                  {t('auction.createAuctionButton')} ({MARKETPLACE_FEES.CREATE_AUCTION.default} {t('auction.coins')})
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setSelectedProduct(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* My Auctions Section */}
        <div className="bg-white rounded-lg shadow-sm p-4 md:p-6">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <h2 className="text-xl md:text-2xl font-bold text-gray-800">
              {t('auction.myAuctions')} 
              <span className="ml-2 text-lg md:text-xl text-yellow-600">({myAuctions.length})</span>
            </h2>
            {myAuctions.length > 0 && (
              <Link
                to="/marketplace/auctions"
                className="text-yellow-600 hover:text-yellow-700 font-semibold text-sm md:text-base"
              >
                ดูประมูลทั้งหมด →
              </Link>
            )}
          </div>

          {/* Search and Filters */}
          {myAuctions.length > 0 && (
            <div className="bg-gray-50 rounded-lg shadow-sm p-4 md:p-6 mb-4 md:mb-6">
              {/* Search Bar */}
              <div className="mb-4">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="ค้นหาประมูล..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 md:py-3 pl-10 md:pl-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  />
                  <svg
                    className="absolute left-3 md:left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
              </div>

              {/* Status Tabs */}
              <div className="mb-4">
                <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
                  {statusFilters.map(filter => (
                    <button
                      key={filter.value}
                      onClick={() => setStatusFilter(filter.value)}
                      className={`flex items-center gap-2 px-3 md:px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
                        statusFilter === filter.value
                          ? 'bg-yellow-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span>{filter.icon}</span>
                      <span className="text-sm md:text-base">{filter.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Sort and View Mode */}
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                {/* Sort Options */}
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm md:text-base text-gray-600">เรียงตาม:</span>
                  <div className="flex items-center gap-2 flex-wrap">
                    {sortOptions.map(option => (
                      <button
                        key={option.value}
                        onClick={() => setSortBy(option.value)}
                        className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                          sortBy === option.value
                            ? 'bg-yellow-500 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        <span>{option.icon}</span>
                        <span>{option.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* View Mode Toggle */}
                <div className="flex items-center gap-2">
                  <span className="text-sm md:text-base text-gray-600">มุมมอง:</span>
                  <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                    {viewModes.map(mode => (
                      <button
                        key={mode.value}
                        onClick={() => setViewMode(mode.value)}
                        className={`px-3 py-1.5 rounded transition-colors ${
                          viewMode === mode.value
                            ? 'bg-yellow-500 text-white'
                            : 'text-gray-700 hover:bg-gray-200'
                        }`}
                        title={mode.label}
                      >
                        <span className="text-base">{mode.icon}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Results Count */}
          {myAuctions.length > 0 && (
            <div className="mb-4 text-sm md:text-base text-gray-600">
              พบ {filteredAuctions.length} ประมูล
            </div>
          )}

          {/* Auctions Display */}
          {myAuctions.length === 0 ? (
            <div className="bg-gray-50 rounded-lg p-8 md:p-12 text-center border-2 border-dashed border-gray-300">
              <div className="text-6xl mb-4">🔨</div>
              <p className="text-lg md:text-xl text-gray-800 mb-2">{t('auction.noAuctions')}</p>
              <p className="text-sm md:text-base text-gray-600 mb-4">
                เริ่มต้นสร้างประมูลสินค้าของคุณเลย!
              </p>
              <button
                onClick={() => setShowCreateForm(true)}
                className="bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
              >
                + สร้างประมูลใหม่
              </button>
            </div>
          ) : filteredAuctions.length > 0 ? (
            <>
              {(() => {
                const totalPages = Math.ceil(filteredAuctions.length / itemsPerPage);
                const paginatedAuctions = filteredAuctions.slice(
                  (currentPage - 1) * itemsPerPage,
                  currentPage * itemsPerPage
                );

                return (
                  <>
                    {/* Grid View */}
                    {viewMode === 'grid' && (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 md:gap-4 mb-6">
                        {paginatedAuctions.map(auction => (
                          <AuctionCard key={auction.id} auction={auction} />
                        ))}
                      </div>
                    )}

                    {/* List View */}
                    {viewMode === 'list' && (
                      <div className="space-y-3 mb-6">
                        {paginatedAuctions.map(auction => {
                          const endTime = getEndTime(auction);
                          const timeRemaining = endTime 
                            ? Math.max(0, Math.floor((endTime.getTime() - new Date().getTime()) / 1000 / 60 / 60))
                            : null;
                          const productImage = auction.productImage || auction.productImages?.[0] || auction.images?.[0];

                          return (
                            <Link key={auction.id} to={`/marketplace/auctions/${auction.id}`} className="block">
                              <div className="bg-white rounded-lg shadow-sm p-3 md:p-4 hover:shadow-md transition-shadow border border-yellow-200">
                                <div className="flex items-center gap-3 md:gap-4">
                                  {/* Image */}
                                  <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-yellow-400 to-yellow-600">
                                    {productImage ? (
                                      <img
                                        src={productImage}
                                        alt={auction.productName}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.parentElement.innerHTML = '<img src="/logo.png" alt="Logo" class="w-full h-full object-contain opacity-80" onerror="this.style.display=\'none\'; this.parentElement.innerHTML=\'<div class=\'text-white text-xl\'>🔨</div>\';" />';
                                        }}
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <img 
                                          src="/logo.png" 
                                          alt="CannaTrade Logo" 
                                          className="w-12 h-12 object-contain opacity-80"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.parentElement.innerHTML = '<div class="text-white text-xl">🔨</div>';
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>

                                  {/* Info */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                      <div className="flex-1 min-w-0">
                                        <h3 className="text-sm md:text-base font-medium text-gray-800 mb-1 line-clamp-1">
                                          {auction.productName}
                                        </h3>
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className="text-xs bg-yellow-500 text-white px-2 py-0.5 rounded">
                                            🔨 ประมูล
                                          </span>
                                          {timeRemaining !== null && timeRemaining > 0 && (
                                            <span className={`text-xs px-2 py-0.5 rounded ${
                                              timeRemaining <= 0 
                                                ? 'bg-red-500 text-white' 
                                                : timeRemaining < 24 
                                                  ? 'bg-orange-500 text-white' 
                                                  : 'bg-green-500 text-white'
                                            }`}>
                                              {timeRemaining < 24 ? `${timeRemaining} ชม.` : `${Math.floor(timeRemaining / 24)} วัน`}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs md:text-sm">
                                      <div>
                                        <span className="text-gray-500">ราคาปัจจุบัน:</span>
                                        <span className="ml-1 font-bold text-red-500">
                                          ฿{auction.currentBid?.toLocaleString() || auction.startingPrice?.toLocaleString()}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">เริ่มต้น:</span>
                                        <span className="ml-1 font-medium text-gray-700">
                                          ฿{auction.startingPrice?.toLocaleString()}
                                        </span>
                                      </div>
                                      <div>
                                        <span className="text-gray-500">ผู้เสนอ:</span>
                                        <span className="ml-1 font-medium text-gray-700">
                                          {auction.bids?.length || 0} คน
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Action Button */}
                                  <div className="flex-shrink-0">
                                    <Link
                                      to={`/marketplace/auctions/${auction.id}`}
                                      className="bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-2 px-4 rounded-lg text-sm transition-colors whitespace-nowrap inline-block"
                                    >
                                      ดูรายละเอียด
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    )}

                    {/* Compact View */}
                    {viewMode === 'compact' && (
                      <div className="space-y-2 mb-6">
                        {paginatedAuctions.map(auction => {
                          const endTime = getEndTime(auction);
                          const timeRemaining = endTime 
                            ? Math.max(0, Math.floor((endTime.getTime() - new Date().getTime()) / 1000 / 60 / 60))
                            : null;
                          const productImage = auction.productImage || auction.productImages?.[0] || auction.images?.[0];

                          return (
                            <Link key={auction.id} to={`/marketplace/auctions/${auction.id}`} className="block">
                              <div className="bg-white rounded-lg shadow-sm p-2 md:p-3 hover:shadow-md transition-shadow border border-yellow-200">
                                <div className="flex items-center gap-3">
                                  {/* Image */}
                                  <div className="w-16 h-16 md:w-20 md:h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gradient-to-br from-yellow-400 to-yellow-600">
                                    {productImage ? (
                                      <img
                                        src={productImage}
                                        alt={auction.productName}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.parentElement.innerHTML = '<img src="/logo.png" alt="Logo" class="w-full h-full object-contain opacity-80" onerror="this.style.display=\'none\'; this.parentElement.innerHTML=\'<div class=\'text-white text-xl\'>🔨</div>\';" />';
                                        }}
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center">
                                        <img 
                                          src="/logo.png" 
                                          alt="CannaTrade Logo" 
                                          className="w-10 h-10 object-contain opacity-80"
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                            e.target.parentElement.innerHTML = '<div class="text-white text-xl">🔨</div>';
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>

                                  {/* Info */}
                                  <div className="flex-1 min-w-0">
                                    <h3 className="text-sm md:text-base font-medium text-gray-800 truncate mb-1">
                                      {auction.productName}
                                    </h3>
                                    <div className="flex items-center gap-2 text-xs md:text-sm">
                                      <span className="font-bold text-red-500">
                                        ฿{auction.currentBid?.toLocaleString() || auction.startingPrice?.toLocaleString()}
                                      </span>
                                      <span className="text-gray-500">•</span>
                                      <span className="text-gray-600">
                                        {auction.bids?.length || 0} ผู้เสนอ
                                      </span>
                                      {timeRemaining !== null && timeRemaining > 0 && (
                                        <>
                                          <span className="text-gray-500">•</span>
                                          <span className={`${
                                            timeRemaining <= 0 
                                              ? 'text-red-500' 
                                              : timeRemaining < 24 
                                                ? 'text-orange-500' 
                                                : 'text-green-500'
                                          }`}>
                                            {timeRemaining < 24 ? `${timeRemaining}ชม.` : `${Math.floor(timeRemaining / 24)}วัน`}
                                          </span>
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  {/* Action Button */}
                                  <div className="flex-shrink-0">
                                    <Link
                                      to={`/marketplace/auctions/${auction.id}`}
                                      className="bg-yellow-500 hover:bg-yellow-600 text-white font-medium py-1.5 px-3 rounded-lg text-xs md:text-sm transition-colors whitespace-nowrap inline-block"
                                    >
                                      ดูรายละเอียด
                                    </Link>
                                  </div>
                                </div>
                              </div>
                            </Link>
                          );
                        })}
                      </div>
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="mt-6 mb-20 md:mb-6">
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={setCurrentPage}
                          itemsPerPage={itemsPerPage}
                          totalItems={filteredAuctions.length}
                          onItemsPerPageChange={(newItemsPerPage) => {
                            setItemsPerPage(newItemsPerPage);
                            setCurrentPage(1);
                          }}
                        />
                      </div>
                    )}
                  </>
                );
              })()}
            </>
          ) : (
            <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 text-center mb-20 md:mb-6">
              <div className="text-6xl mb-4">🔍</div>
              <p className="text-lg md:text-xl text-gray-800 mb-2">ไม่พบประมูลที่ค้นหา</p>
              <p className="text-sm md:text-base text-gray-600">
                ลองเปลี่ยนคำค้นหาหรือตัวกรองดูนะครับ
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Navigation - Mobile Only */}
      <BottomNavigation />
    </div>
  );
};

export default AuctionManagement;



